import React from 'react';
import { useToolbar } from '@ohif/core';

/**
 * Props for the Toolbar component that renders a collection of toolbar buttons and/or button sections.
 *
 * @interface ToolbarProps
 */
interface ToolbarProps {
  /**
   * The section of buttons to display in the toolbar.
   * Common values include 'primary', 'secondary', 'tertiary', etc.
   * Defaults to 'primary' if not specified.
   *
   * @default 'primary'
   */
  buttonSection?: string;

  /**
   * The unique identifier of the viewport this toolbar is associated with.
   */
  viewportId?: string;

  /**
   * The numeric position or location of the toolbar.
   * Used for ordering and layout purposes in the UI.
   */
  location?: number;

  /** Number of primary tools kept fixed at the start of the toolbar. */
  fixedStartCount?: number;
}

export function Toolbar({
  buttonSection = 'primary',
  viewportId,
  location,
  fixedStartCount = 1,
}: ToolbarProps) {
  const {
    toolbarButtons,
    onInteraction,
    isItemOpen,
    isItemLocked,
    openItem,
    closeItem,
    toggleLock,
  } = useToolbar({
    buttonSection,
  });

  if (!toolbarButtons.length) {
    return null;
  }

  const renderToolbarButton = toolDef => (
    <ToolbarButton
      key={toolDef.id}
      toolDef={toolDef}
      viewportId={viewportId}
      location={location}
      onInteraction={onInteraction}
      isItemOpen={isItemOpen}
      isItemLocked={isItemLocked}
      openItem={openItem}
      closeItem={closeItem}
      toggleLock={toggleLock}
    />
  );

  if (buttonSection === 'primary') {
    const startCount = Math.max(1, Math.min(fixedStartCount, toolbarButtons.length));
    const lastButton = toolbarButtons.length > startCount ? toolbarButtons.at(-1) : null;
    const startButtons = toolbarButtons.slice(0, startCount);
    const middleButtons = lastButton ? toolbarButtons.slice(startCount, -1) : [];

    return (
      <div
        className="toolbar-fixed-ends grid w-full min-w-0 items-center"
        style={{ gridTemplateColumns: lastButton ? 'auto minmax(0, 1fr) auto' : 'auto 1fr' }}
      >
        <div className="toolbar-fixed-ends__edge z-10 flex flex-none items-center bg-[#27272b]">
          {startButtons.map(renderToolbarButton)}
        </div>
        <div className="toolbar-fixed-ends__scroller min-w-0 overflow-x-auto overflow-y-hidden">
          <div className="toolbar-fixed-ends__track flex w-max min-w-max items-center">
            {middleButtons.map(renderToolbarButton)}
          </div>
        </div>
        {lastButton && (
          <div className="toolbar-fixed-ends__edge z-10 flex flex-none items-center bg-[#27272b]">
            {renderToolbarButton(lastButton)}
          </div>
        )}
      </div>
    );
  }

  return <>{toolbarButtons.map(renderToolbarButton)}</>;
}

// ToolbarButton 组件，复用按钮渲染逻辑
function ToolbarButton({
  toolDef,
  viewportId,
  location,
  onInteraction,
  isItemOpen,
  isItemLocked,
  openItem,
  closeItem,
  toggleLock,
}) {
  const { id, Component, componentProps } = toolDef;

  // Enhanced props with state and actions - respecting viewport specificity
  const enhancedProps = {
    ...componentProps,
    isOpen: isItemOpen(id, viewportId),
    isLocked: isItemLocked(id, viewportId),
    onOpen: () => openItem(id, viewportId),
    onClose: () => closeItem(id, viewportId),
    onToggleLock: () => toggleLock(id, viewportId),
    viewportId,
  };

  const tool = (
    <Component
      key={id}
      id={id}
      location={location}
      onInteraction={args => {
        onInteraction({
          ...args,
          itemId: id,
          viewportId,
        });
      }}
      {...enhancedProps}
    />
  );

  return <div key={id}>{tool}</div>;
}
